import apiClient from './apiClient';
import type { Task } from './tasksService';

// `whatsapp` is the persisted legacy enum value for the external dispatch
// channel. The backend routes it to Telegram or WATI via EXTERNAL_MESSAGING_CHANNEL.
export type DispatchDeliveryChannel = 'in_app' | 'whatsapp' | 'both';
export type DailyDispatchStatus = 'draft' | 'held' | 'sent' | 'partially_sent' | 'failed' | 'cancelled';
export type DispatchTaskDeliveryStatus = 'pending' | 'sent' | 'delivered' | 'failed' | 'held' | 'skipped';

export interface DailyDispatch {
  id: string;
  projectId: string;
  dispatchDate: string;
  teamId?: string | null;
  status: DailyDispatchStatus;
  title: string;
  notes?: string | null;
  manualHold: boolean;
  deliveryChannel: DispatchDeliveryChannel;
  deliverySummary?: {
    itemCount?: number;
    sentCount?: number;
    failedCount?: number;
    skippedCount?: number;
  } | null;
  generatedBy?: string | null;
  sentBy?: string | null;
  sentAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface DailyDispatchTask {
  id: string;
  dailyDispatchId: string;
  taskId: string;
  teamId?: string | null;
  sequenceOrder: number;
  supervisorNotes?: string | null;
  deliveryStatus: DispatchTaskDeliveryStatus;
  deliveryChannel: DispatchDeliveryChannel;
  deliveryTarget?: string | null;
  deliveryProviderMessageId?: string | null;
  deliveryError?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface DailyDispatchPacket {
  dispatch: DailyDispatch;
  items: DailyDispatchTask[];
}

export interface DailyDispatchConfig {
  id: string;
  projectId: string;
  autoEnabled: boolean;
  timezone: string;
  generateTimeLocal: string;
  sendTimeLocal: string;
  deliveryChannel: DispatchDeliveryChannel;
  includeAheadOfSchedule: boolean;
}

export interface GenerateDailyDispatchPayload {
  projectId: string;
  dispatchDate: string;
  teamId?: string;
  taskIds?: string[];
  workerIds?: string[];
  title?: string;
  notes?: string;
  manualHold?: boolean;
  deliveryChannel?: DispatchDeliveryChannel;
}

type ApiEnvelope<T> = { success?: boolean; data?: T };

const unwrap = <T>(response: any): T => {
  const body = response?.data ?? response;
  const envelope = body as ApiEnvelope<T>;
  return envelope && Object.prototype.hasOwnProperty.call(envelope, 'data')
    ? (envelope.data as T)
    : (body as T);
};

const toQuery = (params: Record<string, unknown>) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });
  return query.toString();
};

class DailyDispatchService {
  async listDispatches(filters: {
    projectId?: string;
    dispatchDate?: string;
    teamId?: string;
    status?: DailyDispatchStatus;
  } = {}): Promise<DailyDispatch[]> {
    const query = toQuery(filters);
    const response = await apiClient.get<ApiEnvelope<DailyDispatch[]>>(
      query ? `/daily-dispatches?${query}` : '/daily-dispatches'
    );
    return unwrap<DailyDispatch[]>(response) || [];
  }

  async getEligibleTasks(filters: {
    projectId: string;
    dispatchDate: string;
    teamId?: string;
    limit?: number;
  }): Promise<Task[]> {
    const query = toQuery(filters);
    const response = await apiClient.get<ApiEnvelope<Task[]>>(`/daily-dispatches/eligible-tasks?${query}`);
    return unwrap<Task[]>(response) || [];
  }

  async getConfig(projectId: string): Promise<DailyDispatchConfig> {
    const response = await apiClient.get<ApiEnvelope<DailyDispatchConfig>>(
      `/daily-dispatches/projects/${projectId}/config`
    );
    return unwrap<DailyDispatchConfig>(response);
  }

  async getDispatch(id: string): Promise<DailyDispatchPacket> {
    const response = await apiClient.get<ApiEnvelope<DailyDispatchPacket>>(`/daily-dispatches/${id}`);
    return unwrap<DailyDispatchPacket>(response);
  }

  async upsertConfig(projectId: string, payload: Partial<Omit<DailyDispatchConfig, 'id' | 'projectId'>>): Promise<DailyDispatchConfig> {
    const response = await apiClient.put<ApiEnvelope<DailyDispatchConfig>>(
      `/daily-dispatches/projects/${projectId}/config`,
      payload
    );
    return unwrap<DailyDispatchConfig>(response);
  }

  async generateDraft(payload: GenerateDailyDispatchPayload): Promise<DailyDispatchPacket> {
    const response = await apiClient.post<ApiEnvelope<DailyDispatchPacket>>('/daily-dispatches', payload);
    return unwrap<DailyDispatchPacket>(response);
  }

  async setManualHold(id: string, manualHold: boolean): Promise<DailyDispatch> {
    const response = await apiClient.patch<ApiEnvelope<DailyDispatch>>(`/daily-dispatches/${id}/hold`, {
      manualHold,
    });
    return unwrap<DailyDispatch>(response);
  }

  async sendDispatch(id: string, deliveryChannel?: DispatchDeliveryChannel): Promise<DailyDispatchPacket> {
    const response = await apiClient.post<ApiEnvelope<DailyDispatchPacket>>(`/daily-dispatches/${id}/send`, {
      deliveryChannel,
    });
    return unwrap<DailyDispatchPacket>(response);
  }
}

export default new DailyDispatchService();
